// Import necessary modules and functions
import moment from "moment-timezone";
import { isNullOrUndefined } from "./objectUtils";

export const formatTime = (
  ms: any,
  fractionDigits = null,
  fallbackValue: string | number | undefined | null | any = undefined,
  noFormat = false
) => {
  if (isNullOrUndefined(ms) || isNaN(ms)) {
    return fallbackValue ?? null;
  }

  if (ms <= 0) return `0 ${noFormat ? "" : "ms"}`;

  let formattedDuration: string;
  if (ms < 1) formattedDuration = `${moment.duration(ms).asMilliseconds().toFixed(2)} ms`;
  else if (ms < 1000) formattedDuration = `${moment.duration(ms).asMilliseconds().toFixed(0)} ms`;
  else if (ms < 60000) {
    formattedDuration = `${formatNumber(
      moment.duration(ms).asSeconds() + "",
      fractionDigits != null ? fractionDigits : 0
    )} ${noFormat ? "" : "s"}`;
  } else if (ms < 3600000) {
    formattedDuration = `${formatNumber(
      moment.duration(ms).asMinutes() + "",
      fractionDigits != null ? fractionDigits : 1
    )} ${noFormat ? "" : "m"}`;
  } else if (ms < 86400000) {
    formattedDuration = `${formatNumber(
      moment.duration(ms).asHours() + "",
      fractionDigits != null ? fractionDigits : 1
    )} ${noFormat ? "" : "h"}`;
  } else {
    formattedDuration = `${formatNumber(
      moment.duration(ms).asDays() + "",
      fractionDigits != null ? fractionDigits : 1
    )} ${noFormat ? "" : "d"}`;
  }
  return formattedDuration;
};

export const formatNumber = (
  value: any,
  maximumFractionDigits = 1,
  nanToZero = true,
  fallback: string | undefined = undefined
) => {
  if (typeof maximumFractionDigits === "object") {
    //TODO:for some reason its coming from apexchart with a series object
    maximumFractionDigits = 1;
  }

  if (isNullOrUndefined(value)) return fallback ?? value;
  let numberValue = value;
  if (typeof value === "string") {
    numberValue = stringToNumber(value);
  }
  if (nanToZero && Number.isNaN(numberValue)) {
    //use fallback instead
    return 0;
  } else if (fallback && Number.isNaN(numberValue)) {
    return fallback;
  }
  //todo: is it correct to override parameters
  if (+numberValue != 0) {
    if (Math.abs(+numberValue) < 0.1) {
      //0.02
      maximumFractionDigits = 2;
    } else if (Math.abs(+numberValue) < 1) {
      //0.5
      maximumFractionDigits = 1;
    }
  } else {
    maximumFractionDigits = 0;
  }

  return new Intl.NumberFormat("en-US", { maximumFractionDigits }).format(+numberValue);
};

const stringToNumber = (str: any) => {
  if (isNullOrUndefined(str)) return str;
  if (typeof str === "number") {
    return str;
  }
  if (str.includes(".")) {
    return Number.parseFloat(str.replace("%", "").replace(",", ""));
  } else {
    return Number.parseInt(str.replace("%", "").replace(",", ""));
  }
};

export const chartNumberFormatter = (value: any) => {
  if (value < 1) {
    return formatNumber(value, 2);
  }

  return formatNumber(value, 0);
};

export const formatCost = (value: any, noDecimals = false) => {
  if (isNullOrUndefined(value)) return value;
  let numberValue = value;
  if (typeof value === "string") {
    numberValue = stringToNumber(value);
  }
  if (Number.isNaN(numberValue)) {
    return 0;
  }

  const parts = numberValue?.toFixed(2)?.split(".");
  const left = parts[0];
  const right = parts[1] || "00";

  const leftArr = left.split("");
  const reversedNum = leftArr.reverse().join("");

  const chunks: any = [];
  for (let i = 0; i < reversedNum.length; i += 1) {
    if (i % 3 === 0) chunks.push(",");
    chunks.push(reversedNum[i]);
  }

  chunks.shift();
  const leftPart = chunks.reverse().join("");
  return noDecimals ? `$${leftPart}` : `$${leftPart}.${right}`;
};

export const formatPercentage = (
  value: any,
  maximumFractionDigits = 0,
  fallbackValue: string | number | undefined | null = undefined
) => {
  if (isNullOrUndefined(value) || isNaN(value)) {
    return fallbackValue ?? value;
  }
  return `${formatNumber(value, maximumFractionDigits)}%`;
};

export const formatGB = (
  gb: any,
  unitOnly = false,
  numberOnly = false,
  maximumFractionDigits = 1
) => {
  if (isNullOrUndefined(gb)) return "N/A";
  if (gb < 1) return `${formatNumber(gb)} GB`;

  const sizes = ["GB", "TB", "PB", "EB", "ZB", "YB"];
  //@ts-ignore
  const i = parseInt(Math.floor(Math.log(gb) / Math.log(1024)));

  let num = 0;
  if (i == 0) {
    num = gb;
  } else {
    num = gb / Math.pow(1024, i);
  }

  if (numberOnly === true) {
    return formatNumber(num);
  }
  const unit = sizes[i];

  if (unitOnly === true) {
    return unit;
  }

  return `${formatNumber(num, maximumFractionDigits)} ${unit}`;
};

export const formatBytes = (bytes: any, decimals = 0) => {
  if (bytes === 0) return "0 Bytes";
  // debugger;
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${formatNumber(bytes / Math.pow(k, i), dm)} ${sizes[i]}`;
};

// check formatMB
export const formatMB = (
  mb: any,
  unitOnly = false,
  numberOnly = false,
  maximumFractionDigits = 1
) => {
  if (isNullOrUndefined(mb)) return mb;
  if (mb < 1) return `${formatNumber(mb)} MB`;

  const sizes = ["MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(mb) / Math.log(1024));

  const num = mb / Math.pow(1024, i);

  if (numberOnly === true) {
    return formatNumber(num);
  }
  const unit = sizes[i];

  if (unitOnly === true) {
    return unit;
  }

  const formatterNumber = formatNumber(num, maximumFractionDigits);
  return `${formatterNumber} ${unit}`;
};

export const formatDays = (days: any) => {
  if (isNullOrUndefined(days)) return `${formatNumber(0)} days`;
  if (days === 0) return `0 days`;

  if (days == 1) {
    return `${days} day`;
  } else if (days < 31) {
    return `${days} days`;
  } else if (days >= 31) {
    const m = Math.round(days / 31);
    return m == 1 ? `${m} month` : `${m} months`;
  } else if (days >= 365) {
    const y = Math.round(days / 365);
    return y == 1 ? `${y} year` : `${y} years`;
  }
};

export const calculatePercentageChange = (previous: any, current: any) => {
  if (isNullOrUndefined(previous) || previous === 0) return 0;

  if (isNullOrUndefined(current) || current === 0) return -100;

  const change = ((current - previous) / previous) * 100;

  return change;
};

export const abbreviateNumber = (number: number) => {
  if (isNullOrUndefined(number)) {
    return null;
  }
  if (number < 1e3) return formatNumber(number, 1);
  if (number >= 1e3 && number < 1e6) return +(number / 1e3).toFixed(1) + " K";
  if (number >= 1e6 && number < 1e9) return +(number / 1e6).toFixed(1) + " M";
  if (number >= 1e9 && number < 1e12) return +(number / 1e9).toFixed(1) + " B";
  if (number >= 1e12) return +(number / 1e12).toFixed(1) + " T";
};

export const formatTimeStringToMinutes = (timeString: string | null | undefined): string => {
  if (!timeString) return "0 m";

  try {
    const timeParts = timeString.split(":");
    if (timeParts.length !== 3) return "0 m";

    const [hours, minutes, seconds] = timeParts.map((part) => {
      const num = parseInt(part, 10);
      return isNaN(num) ? 0 : num;
    });

    const totalMinutes = hours * 60 + minutes + Math.round(seconds / 60);

    const days = Math.floor(totalMinutes / (24 * 60));
    const remainingHours = Math.floor((totalMinutes % (24 * 60)) / 60);
    const remainingMinutes = totalMinutes % 60;

    const parts: string[] = [];
    if (days > 0) parts.push(`${days} d`);
    if (remainingHours > 0) parts.push(`${remainingHours} h`);
    if (remainingMinutes > 0) parts.push(`${remainingMinutes} m`);

    return parts.length > 0 ? parts.join(" ") : "0 m";
  } catch (e) {
    return "0 m";
  }
};

export const formatDotNetTimeSpanToMinutes = (
  timeSpanString: string | null | undefined
): string => {
  if (!timeSpanString) return "0 m";

  try {
    // Split by decimal point first to handle days
    const daysSplit = timeSpanString.split(".");

    let days = 0;
    let timeString = timeSpanString;

    // If we have days part
    if (daysSplit.length > 1) {
      days = parseInt(daysSplit[0], 10);
      timeString = daysSplit[1]; // The rest of the string after the dot
    }

    const timeParts = timeString.split(":");
    if (timeParts.length !== 3) return "0 m";

    const [hours, minutes, seconds] = timeParts.map((part) => {
      const num = parseInt(part, 10);
      return isNaN(num) ? 0 : num;
    });

    const totalMinutes = days * 24 * 60 + hours * 60 + minutes + Math.round(seconds / 60);

    const resultDays = Math.floor(totalMinutes / (24 * 60));
    const remainingHours = Math.floor((totalMinutes % (24 * 60)) / 60);
    const remainingMinutes = totalMinutes % 60;

    const parts: string[] = [];
    if (resultDays > 0) parts.push(`${resultDays} d`);
    if (remainingHours > 0) parts.push(`${remainingHours} h`);
    if (remainingMinutes > 0) parts.push(`${remainingMinutes} m`);

    return parts.length > 0 ? parts.join(" ") : "0 m";
  } catch (e) {
    return "0 m";
  }
};

export const calcPercent = (totalNum: number, used: number) => {
  if (!totalNum) return 0;
  return Math.round((used / totalNum) * 100);
};
